"use client";

import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router';
import {useAppNavigate} from '../../lib/use-app-navigate';
import OrderCard from './OrderCard';
import Header from '../../components/Private/Header';
import {Spinner} from '@heroui/react';
import ModalFilter from '../../components/Private/ModalFilter';
import {request} from '../../lib/request';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import PaginationApp from '../../components/Pagination';
import {formatGregorianDateToJalali} from '../../lib/formatJalaliDate';

const FILTER_LABELS = {
    user_name: 'نام مشتری',
    mobile: 'موبایل',
    from_date: 'از تاریخ',
    to_date: 'تا تاریخ',
    status: 'وضعیت',
    payment_method: 'پرداخت',
};

const STATUS_LABELS = {
    pending: 'در انتظار تایید',
    processing: 'در آماده‌سازی',
    completed: 'در انتظار پیک',
    delivery: 'تحویل شده',
    cancelled: 'کنسل شده',
};

const PAYMENT_LABELS = {
    cash: 'پرداخت در محل',
    online: 'پرداخت آنلاین',
};

const OrdersList = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useAppNavigate();
    const [loginBanner, setLoginBanner] = useState(null);

    const filters = useMemo(
        () => ({
            user_name: searchParams.get('user_name') || '',
            mobile: searchParams.get('mobile') || '',
            from_date: searchParams.get('from_date') || '',
            to_date: searchParams.get('to_date') || '',
            status: searchParams.get('status') || '',
            payment_method: searchParams.get('payment_method') || '',
        }),
        [searchParams]
    );

    const page = Number(searchParams.get('page') || '1');
    const currentPage = isNaN(page) || page < 1 ? 1 : page;

    useEffect(() => {
        if (!location.state?.loginSuccess) {
            return;
        }

        setLoginBanner(location.state.loginSuccess);
        navigate(
            {pathname: location.pathname, search: location.search},
            {replace: true, state: {}}
        );
    }, [location.pathname, location.search, location.state, navigate]);

    useEffect(() => {
        if (!loginBanner) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => setLoginBanner(null), 4500);
        return () => window.clearTimeout(timeoutId);
    }, [loginBanner]);

    const cleanQueryParams = useMemo(() => {
        const params = {};

        Object.entries(filters).forEach(([key, value]) => {
            if (value) params[key] = value;
        });

        if (currentPage > 1) {
            params.page = currentPage.toString();
        }

        return params;
    }, [filters, currentPage]);

    const {data, isLoading, isFetching, refetch} = useQuery({
        queryKey: ['orders', cleanQueryParams],
        queryFn: async () =>
            await request({
                method: 'get',
                url: '/restaurant/order/index_order',
                query: cleanQueryParams,
            }),
        placeholderData: keepPreviousData,
    });

    const orders = data?.data || [];
    const totalOrders = data?.total ?? orders.length;
    const lastPage = Number(data?.last_page) || 1;

    const activeFilters = useMemo(() => {
        return Object.entries(filters)
            .filter(([, value]) => Boolean(value))
            .map(([key, value]) => {
                let label = value;
                if (key === 'status') label = STATUS_LABELS[value] || value;
                if (key === 'payment_method') label = PAYMENT_LABELS[value] || value;
                if (key === 'from_date' || key === 'to_date') {
                    label = formatGregorianDateToJalali(value);
                }
                return {key, label: FILTER_LABELS[key], value: label};
            });
    }, [filters]);

    const clearFilter = (key) => {
        const params = new URLSearchParams(searchParams);
        params.delete(key);
        params.delete('page');
        navigate(`?${params.toString()}`, {replace: true});
    };

    const clearAllFilters = () => {
        navigate('/orders', {replace: true});
    };

    const handlePageChange = (newPage) => {
        const params = new URLSearchParams(searchParams);
        if (newPage === 1) {
            params.delete('page');
        } else {
            params.set('page', newPage.toString());
        }
        navigate(`?${params.toString()}`, {replace: true});
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const updateFilters = (newFilters) => {
        const params = new URLSearchParams(searchParams);

        Object.entries(newFilters).forEach(([key, value]) => {
            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }
        });

        params.delete('page');
        navigate(`?${params.toString()}`, {replace: true});
    };

    return (
        <section className="rest-orders-page">
            <Header title="لیست سفارشات">
                <ModalFilter filters={filters} setFilters={updateFilters}/>
            </Header>

            {loginBanner ? (
                <div className="rest-login-banner" role="status">
                    <span className="rest-login-banner__title">ورود موفق</span>
                    <p className="rest-login-banner__text">{loginBanner}</p>
                    <button
                        type="button"
                        className="rest-login-banner__close"
                        onClick={() => setLoginBanner(null)}
                        aria-label="بستن پیام ورود"
                    >
                        ×
                    </button>
                </div>
            ) : null}

            {activeFilters.length > 0 && (
                <div className="rest-orders-filters">
                    <div className="rest-orders-filters__chips">
                        {activeFilters.map(({key, label, value}) => (
                            <button
                                key={key}
                                type="button"
                                className="rest-orders-filters__chip"
                                onClick={() => clearFilter(key)}
                                aria-label={`حذف فیلتر ${label}`}
                            >
                                <span className="rest-orders-filters__chip-label">{label}:</span>
                                <span>{value}</span>
                                <span className="rest-orders-filters__chip-close" aria-hidden="true">×</span>
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="rest-orders-filters__clear"
                        onClick={clearAllFilters}
                    >
                        پاک کردن همه
                    </button>
                </div>
            )}

            <div className="rest-orders-content">
                {isLoading ? (
                    <div className="rest-orders-state rest-orders-state--loading">
                        <Spinner color="warning"/>
                        <p>در حال بارگذاری سفارش‌ها...</p>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="rest-orders-state rest-orders-state--empty">
                        <img
                            src="/images/Empty page.png"
                            alt=""
                            className="rest-orders-state__image"
                        />
                        <p className="rest-orders-state__title">سفارشی یافت نشد</p>
                        <p className="rest-orders-state__text">
                            {activeFilters.length > 0
                                ? 'با این فیلترها سفارشی وجود ندارد. فیلترها را تغییر دهید.'
                                : 'هنوز سفارشی ثبت نشده است.'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="rest-orders-toolbar">
                            <p className="rest-orders-toolbar__count">
                                {totalOrders.toLocaleString('fa-IR')} سفارش
                                {lastPage > 1
                                    ? ` — صفحه ${currentPage.toLocaleString('fa-IR')} از ${lastPage.toLocaleString('fa-IR')}`
                                    : ''}
                                {isFetching && !isLoading ? ' (در حال بروزرسانی...)' : ''}
                            </p>
                        </div>

                        {!!data?.order && (
                            <div className="rest-orders-alert" role="alert">
                                <span className="rest-orders-alert__icon" aria-hidden="true">!</span>
                                <p>
                                    شما سفارش زمان‌دار دارید که هنوز تحویل داده نشده است.
                                </p>
                            </div>
                        )}

                        <div className="rest-orders-list">
                            {orders.map((order) => (
                                <OrderCard key={order.id} refetch={refetch} order={order}/>
                            ))}
                        </div>

                        <div className="rest-orders-pagination">
                            <PaginationApp
                                pages={lastPage}
                                currentPage={currentPage}
                                onChange={handlePageChange}
                            />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default OrdersList;
